import React from 'react';
import { motion } from 'framer-motion';
import Seo from '../../components/Seo';
import { seo } from '../../seo/seoData';
import { useTranslation } from 'react-i18next';
import { Shield, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import LegalDraftBanner from '../../components/LegalDraftBanner';
import { COMPANY_LEGAL } from '../../config/companyLegalIdentity.generated';

// =============================================================================
// LEGAL DRAFT — REQUIRES AVOCAT REVIEW BEFORE B2C PUBLIC LAUNCH
// =============================================================================
// The Mentions Légales / RGPD content was scaffolded by an earlier session.
// Known LCEN Art 6 III + RGPD Art 13/14 gaps to close before launch:
//   - SIRET / SIREN (953 195 724) not displayed
//   - Full registered address (6 rue Antoine de Saint-Exupéry, 44300 Nantes)
//     not displayed — current text shows only "Nantes (44), France"
//   - Share capital (mandatory for SAS) not declared
//   - RCS de Nantes registration not stated
//   - VAT number (TVA intracommunautaire) missing
//   - VTC license number / inscription in registre des exploitateurs VTC
//     (Article L. 3122 Code des transports) missing — mandatory for VTC
//     operator publication
//   - Hosting provider details vague — must list Hetzner Online GmbH,
//     Industriestr. 25, 91710 Gunzenhausen with phone
//   - Subprocessors list incomplete (Twilio / Resend / Sentry not declared)
//   - Cookie consent mechanism not implemented (text mentions cookies but
//     no banner exists on the site)
//
// FR is authoritative; non-FR versions are courtesy translations marked via
// LegalDraftBanner.
// =============================================================================

const MentionsLegalesRGPDPage = () => {
  const { t } = useTranslation();

  const editorRows = [
    ['raisonSociale', 'raisonSocialeValue'],
    ['activite', 'activiteValue'],
    ['siege', 'siegeValue'],
    ['phone', null], // value is hardcoded phone
    ['email', null], // value is hardcoded email
    ['dirPubli', 'dirPubliValue'],
  ];

  return (
    <div className="min-h-screen bg-black pt-20" data-testid="mentions-legales-page">
          <Seo {...seo.mentionsLegales} />
      <LegalDraftBanner />
      <section className="py-24 md:py-32">
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Back link */}
            <Link to="/" className="inline-flex items-center gap-2 text-neutral-400 hover:text-white transition-colors mb-8">
              <ArrowLeft className="w-4 h-4" />
              {t('legalPages.common.backToHome')}
            </Link>

            {/* Header */}
            <div className="flex items-center gap-4 mb-12">
              <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight">
                  {t('legalPages.mentionsLegalesRGPD.title')}
                </h1>
                <p className="text-neutral-500 text-sm mt-1">{t('legalPages.common.lastUpdated')}</p>
              </div>
            </div>

            <div className="prose prose-invert max-w-none space-y-10">
              {/* 1. Éditeur du site */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">
                  {t('legalPages.mentionsLegalesRGPD.editor.heading')}
                </h2>
                <div className="text-neutral-400 leading-relaxed space-y-2">
                  <p><strong className="text-white">{t('legalPages.mentionsLegalesRGPD.editor.raisonSocialeLabel')}</strong> {COMPANY_LEGAL.legalName} ({COMPANY_LEGAL.legalForm})</p>
                  <p><strong className="text-white">{t('legalPages.mentionsLegalesRGPD.editor.brandLabel')}</strong> {COMPANY_LEGAL.brand}</p>
                  <p><strong className="text-white">{t('legalPages.mentionsLegalesRGPD.editor.capitalLabel')}</strong> {COMPANY_LEGAL.shareCapital}</p>
                  <p><strong className="text-white">{t('legalPages.mentionsLegalesRGPD.editor.activiteLabel')}</strong> {t('legalPages.mentionsLegalesRGPD.editor.activiteValue')}</p>
                  <p><strong className="text-white">{t('legalPages.mentionsLegalesRGPD.editor.siegeLabel')}</strong> {COMPANY_LEGAL.address.street}, {COMPANY_LEGAL.address.postalCode} {COMPANY_LEGAL.address.city}, {COMPANY_LEGAL.address.country}</p>
                  <p><strong className="text-white">{t('legalPages.mentionsLegalesRGPD.editor.sirenLabel')}</strong> {COMPANY_LEGAL.siren}</p>
                  <p><strong className="text-white">{t('legalPages.mentionsLegalesRGPD.editor.siretLabel')}</strong> {COMPANY_LEGAL.siret}</p>
                  <p><strong className="text-white">{t('legalPages.mentionsLegalesRGPD.editor.rcsLabel')}</strong> {COMPANY_LEGAL.rcs}</p>
                  <p><strong className="text-white">{t('legalPages.mentionsLegalesRGPD.editor.tvaLabel')}</strong> {COMPANY_LEGAL.vat}</p>
                  <p><strong className="text-white">{t('legalPages.mentionsLegalesRGPD.editor.apeLabel')}</strong> {COMPANY_LEGAL.apeNaf}</p>
                  <p><strong className="text-white">{t('legalPages.mentionsLegalesRGPD.editor.phoneLabel')}</strong> {COMPANY_LEGAL.phone}</p>
                  <p><strong className="text-white">{t('legalPages.mentionsLegalesRGPD.editor.emailLabel')}</strong> {COMPANY_LEGAL.email}</p>
                  <p><strong className="text-white">{t('legalPages.mentionsLegalesRGPD.editor.dirPubliLabel')}</strong> {COMPANY_LEGAL.directorOfPublication}</p>
                </div>
              </section>

              {/* 2. Hébergement */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">
                  {t('legalPages.mentionsLegalesRGPD.hosting.heading')}
                </h2>
                <p className="text-neutral-400 leading-relaxed">
                  {COMPANY_LEGAL.host.name}, {COMPANY_LEGAL.host.street}, {COMPANY_LEGAL.host.postalCode} {COMPANY_LEGAL.host.city}, {COMPANY_LEGAL.host.country}
                </p>
              </section>

              {/* 3. RGPD */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">
                  {t('legalPages.mentionsLegalesRGPD.rgpd.heading')}
                </h2>

                <h3 className="font-heading text-lg font-medium text-white mt-6 mb-3">
                  {t('legalPages.mentionsLegalesRGPD.rgpd.sub31Title')}
                </h3>
                <p className="text-neutral-400 leading-relaxed">
                  {t('legalPages.mentionsLegalesRGPD.rgpd.sub31Body')}
                </p>

                <h3 className="font-heading text-lg font-medium text-white mt-6 mb-3">
                  {t('legalPages.mentionsLegalesRGPD.rgpd.sub32Title')}
                </h3>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.mentionsLegalesRGPD.rgpd.sub32Intro')}</p>
                <ul className="list-disc list-inside text-neutral-400 mt-4 space-y-2">
                  {[1, 2, 3, 4].map(i => (
                    <li key={i}>{t(`legalPages.mentionsLegalesRGPD.rgpd.sub32List${i}`)}</li>
                  ))}
                </ul>

                <h3 className="font-heading text-lg font-medium text-white mt-6 mb-3">
                  {t('legalPages.mentionsLegalesRGPD.rgpd.sub33Title')}
                </h3>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.mentionsLegalesRGPD.rgpd.sub33Intro')}</p>
                <ul className="list-disc list-inside text-neutral-400 mt-4 space-y-2">
                  {[1, 2, 3, 4, 5].map(i => (
                    <li key={i}>{t(`legalPages.mentionsLegalesRGPD.rgpd.sub33List${i}`)}</li>
                  ))}
                </ul>

                <h3 className="font-heading text-lg font-medium text-white mt-6 mb-3">
                  {t('legalPages.mentionsLegalesRGPD.rgpd.sub34Title')}
                </h3>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.mentionsLegalesRGPD.rgpd.sub34Body')}</p>

                <h3 className="font-heading text-lg font-medium text-white mt-6 mb-3">
                  {t('legalPages.mentionsLegalesRGPD.rgpd.sub35Title')}
                </h3>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.mentionsLegalesRGPD.rgpd.sub35Body')}</p>

                <h3 className="font-heading text-lg font-medium text-white mt-6 mb-3">
                  {t('legalPages.mentionsLegalesRGPD.rgpd.sub36Title')}
                </h3>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.mentionsLegalesRGPD.rgpd.sub36Body')}</p>
              </section>

              {/* 4. Vos droits */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">
                  {t('legalPages.mentionsLegalesRGPD.rights.heading')}
                </h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.mentionsLegalesRGPD.rights.intro')}</p>
                <ul className="list-disc list-inside text-neutral-400 mt-4 space-y-2">
                  {[
                    ['accessLabel', 'accessDesc'],
                    ['rectifLabel', 'rectifDesc'],
                    ['eraseLabel', 'eraseDesc'],
                    ['limitLabel', 'limitDesc'],
                    ['portLabel', 'portDesc'],
                    ['objectLabel', 'objectDesc'],
                  ].map(([labelKey, descKey]) => (
                    <li key={labelKey}>
                      <strong className="text-white">{t(`legalPages.mentionsLegalesRGPD.rights.${labelKey}`)}</strong>{' '}
                      {t(`legalPages.mentionsLegalesRGPD.rights.${descKey}`)}
                    </li>
                  ))}
                </ul>
                <p className="text-neutral-400 leading-relaxed mt-4">
                  {t('legalPages.mentionsLegalesRGPD.rights.contactRGPD')} <strong className="text-white">rgpd@blackmatte.fr</strong>
                </p>
                <p className="text-neutral-400 leading-relaxed mt-4">
                  {t('legalPages.mentionsLegalesRGPD.rights.cnil')}
                </p>
              </section>

              {/* 5. Cookies */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">
                  {t('legalPages.mentionsLegalesRGPD.cookies.heading')}
                </h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.mentionsLegalesRGPD.cookies.intro')}</p>
                <ul className="list-disc list-inside text-neutral-400 mt-4 space-y-2">
                  {[
                    ['essentialLabel', 'essentialDesc'],
                    ['sessionLabel', 'sessionDesc'],
                    ['analyticsLabel', 'analyticsDesc'],
                  ].map(([labelKey, descKey]) => (
                    <li key={labelKey}>
                      <strong className="text-white">{t(`legalPages.mentionsLegalesRGPD.cookies.${labelKey}`)}</strong>{' '}
                      {t(`legalPages.mentionsLegalesRGPD.cookies.${descKey}`)}
                    </li>
                  ))}
                </ul>
                <p className="text-neutral-400 leading-relaxed mt-4">
                  {t('legalPages.mentionsLegalesRGPD.cookies.manage')}
                </p>
              </section>

              {/* 6. Propriété intellectuelle */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">
                  {t('legalPages.mentionsLegalesRGPD.ip.heading')}
                </h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.mentionsLegalesRGPD.ip.p1')}</p>
                <p className="text-neutral-400 leading-relaxed mt-4">{t('legalPages.mentionsLegalesRGPD.ip.p2')}</p>
              </section>

              {/* 7. Litiges */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">
                  {t('legalPages.mentionsLegalesRGPD.litiges.heading')}
                </h2>
                <p className="text-neutral-400 leading-relaxed">{t('legalPages.mentionsLegalesRGPD.litiges.body')}</p>
              </section>

              {/* 8. Contact */}
              <section className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">
                  {t('legalPages.mentionsLegalesRGPD.contactSection.heading')}
                </h2>
                <div className="text-neutral-400 space-y-2">
                  <p>Black Matte</p>
                  <p>Nantes (44), France</p>
                  <p>Téléphone : 09 39 24 23 20</p>
                  <p>WhatsApp : 07 66 30 15 03</p>
                  <p>Email : contact@blackmatte.fr</p>
                  <p>RGPD : rgpd@blackmatte.fr</p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MentionsLegalesRGPDPage;
